import React, { useState, useEffect } from 'react';
import { Collapse, Button, Modal, Form, Input, DatePicker, Select, List, message, InputNumber } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axios from './Services/Api';

const { Option } = Select;
const { Panel } = Collapse;

const Production = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [productionId, setProductionId] = useState('');
  const [productionEntries, setProductionEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); // State for search input
  const [form] = Form.useForm();

  useEffect(() => {
    fetchRestaurants();
    fetchProductionEntries();
  }, []);

  useEffect(() => {
    if (restaurants.length > 0) {
      const defaultRestaurant = restaurants[0]; // Assuming the API returns the default restaurant first
      setSelectedRestaurant(defaultRestaurant._id);
      form.setFieldsValue({ restaurant: defaultRestaurant._id }); // Set default restaurant in the form
      fetchMenuItems(defaultRestaurant._id);
    }
  }, [restaurants]);
  

  useEffect(() => {
    const filtered = menuItems.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredMenuItems(filtered);
  }, [searchTerm, menuItems]);

  const getVendorId = () => {
    const vendorId = localStorage.getItem("vendorId");
    if (!vendorId) {
      message.error("Vendor ID is missing. Please log in again.");
      console.error("Vendor ID not found in localStorage");
      return null;
    }
    return vendorId;
  };

  const fetchRestaurants = async () => {
    const vendorId = getVendorId();
    if (!vendorId) return;
  
    try {
      const { data } = await axios.get(`/getRestaurants?vendorId=${vendorId}`);
      // Filter only default=true restaurants
      const defaultRestaurants = data.filter(r => r.default === true);
  
      if (defaultRestaurants.length === 0) {
        message.warning("No default restaurants found.");
      }
  
      setRestaurants(defaultRestaurants);
    } catch (error) {
      message.error("Failed to fetch restaurants.");
      console.error("Error fetching restaurants:", error);
    }
  };
  
  
  const fetchMenuItems = async (restaurantId) => {
    if (!restaurantId) return;

    try {
      const { data } = await axios.get(`/getMenuItems?restaurantId=${restaurantId}`);
      setMenuItems(data);
      setFilteredMenuItems(data);
    } catch (error) {
      message.error("Failed to fetch menu items.");
      console.error("Error fetching menu items:", error);
    }
  };

  const fetchProductionEntries = async () => {
    const vendorId = getVendorId();
    if (!vendorId) return;

    try {
      const { data } = await axios.get(`/getProductions?vendorId=${vendorId}`);
      if (Array.isArray(data)) {
        setProductionEntries(data);
      } else {
        message.error("Invalid data format received.");
      }
    } catch (error) {
      message.error("Failed to fetch production entries.");
      console.error("Error fetching production entries:", error);
    }
  };

  const generateProductionId = async () => {
  const vendorId = getVendorId();
  if (!vendorId) return;

  try {
    // Ensure we are fetching productions only for the logged-in vendor
    const { data } = await axios.get(`/getProductions?vendorId=${vendorId}`);
    
    // If no entries exist, start with 1
    const nextEntryCount = Array.isArray(data) && data.length > 0 ? data.length + 1 : 1;

    const date = new Date();
    const monthAbbr = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const year = date.getFullYear().toString().slice(-2);
    const newProductionId = `DZY${monthAbbr}${year}${nextEntryCount.toString().padStart(2, '0')}`;

    setProductionId(newProductionId);
  } catch (error) {
    message.error("Failed to generate production ID.");
    console.error("Error generating production ID:", error);
    setProductionId("DZY0001"); // Fallback Production ID
  }
};

  
  const handleAddClick = async () => {
    await generateProductionId();
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    form.resetFields();
    setFilteredMenuItems(menuItems);
    setQuantities({});
    setSearchTerm(""); // Reset search field on modal close
  };

  const handleRestaurantChange = (value) => {
    setSelectedRestaurant(value);
    form.setFieldsValue({ restaurant: value }); // sync with form
    fetchMenuItems(value);
  };
  

  const handleQuantityChange = (itemId, value) => {
    setQuantities(prev => ({ ...prev, [itemId]: value }));
  };

  const handleSubmit = async (values) => {
    const vendorId = getVendorId();
    if (!vendorId) return;

    if (!selectedRestaurant) {
      message.error("Please select a restaurant before submitting.");
      return;
    }

   // Remove zero quantities before saving
const cleanedQuantities = {};

Object.entries(quantities).forEach(([itemId, unitObj]) => {
  const filteredUnits = {};

  Object.entries(unitObj).forEach(([unit, qty]) => {
    if (Number(qty) > 0) {
      filteredUnits[unit] = Number(qty);
    }
  });

  if (Object.keys(filteredUnits).length > 0) {
    cleanedQuantities[itemId] = filteredUnits;
  }
});

const newEntry = {
  date: values.date.format('DD/MM/YY'),
  productionid: productionId,
  batch: values.batch,
  restaurantId: selectedRestaurant,
  vendorId,
  quantities: cleanedQuantities,
};


    try {
      await axios.post('/addproduction', newEntry);
      message.success("Production entry saved successfully!");
      handleModalClose();
      fetchProductionEntries();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to save production entry.");
      console.error("Error saving production entry:", error);
    }
  };

  const getUnitsForItem = (item) => {
  let units = [];

  // Weight-based prices
  if (item.price1kg) units.push("1 kg");
  if (item.price500gm) units.push("500 gm");
  if (item.price250gm) units.push("250 gm");

  // Custom Units (like 5 piece, 10 piece etc.)
  if (Array.isArray(item.customUnits) && item.customUnits.length > 0) {
    item.customUnits.forEach((cu) => {
      units.push(`${cu.qty} ${cu.unit}`);
    });
  }

  return units;
};


  return (
    <div>
      <Button type="primary" onClick={handleAddClick}>ADD +</Button>

      <Collapse accordion>
  {productionEntries.length > 0 ? (
    productionEntries.map((entry, index) => (
      <Panel
        key={entry._id || index}
        header={
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "8px",
              fontSize: "12px",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span><strong>🔢 S.No:</strong> {index + 1}</span>
            <span><strong>📆 Date:</strong> {entry.date}</span>
            <span><strong>🆔 Production ID:</strong> {entry.productionid}</span>
            <span><strong>#️⃣ Batch:</strong> {entry.batch}</span>
          </div>
        }
      >
        <h4 style={{ fontSize: "15px", marginBottom: "8px" }}>Menu Items & Quantities</h4>
        {entry.quantities && typeof entry.quantities === "object" ? (
  <table
    style={{
      width: "95%",
      marginTop: "10px",
      textAlign: "center",
      borderCollapse: "collapse",
      fontSize: "13px",
    }}
  >
    <thead>
      <tr>
        <th style={{ border: "1px solid #ccc", padding: "6px" }}>
          Item ID
        </th>
        <th style={{ border: "1px solid #ccc", padding: "6px" }}>
          Unit
        </th>
        <th style={{ border: "1px solid #ccc", padding: "6px" }}>
          Quantity
        </th>
      </tr>
    </thead>
    <tbody>
  {Object.entries(entry.quantities).flatMap(([itemId, unitObj]) => {
    const menuItem = menuItems.find(item => item._id === itemId);

    if (!menuItem) return [];

    return Object.entries(unitObj).map(([unitLabel, qty]) => (
      <tr key={`${itemId}-${unitLabel}`}>
        <td style={{ border: "1px solid #ccc", padding: "6px" }}>
          {menuItem.name}
        </td>
        <td style={{ border: "1px solid #ccc", padding: "6px" }}>
          {unitLabel}
        </td>
        <td style={{ border: "1px solid #ccc", padding: "6px" }}>
          {qty}
        </td>
      </tr>
    ));
  })}
</tbody>



  </table>
) : (
  <p>No quantities found.</p>
)}



      </Panel>
    ))
  ) : (
    <p style={{ fontSize: "12px" }}>No production entries available.</p>
  )}
</Collapse>



      <Modal title="Add Production Entry" open={isModalOpen} onCancel={handleModalClose} footer={null} width={900}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item label="Production Id" name="productionid" initialValue={productionId} rules={[{ required: true }]}><Input disabled /></Form.Item>
          <Form.Item label="Date" name="date" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item label="Batch Code" name="batch" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item label="Brand" name="restaurant" rules={[{ required: true }]}><Select placeholder="Select Restaurant" onChange={handleRestaurantChange}  value={selectedRestaurant}>{restaurants.map(restaurant => (<Option key={restaurant._id} value={restaurant._id}>{restaurant.name}</Option>))}</Select></Form.Item>
          
          {/* Search Bar */}
          <Input
  placeholder="Search menu item..."
  onChange={(e) => setSearchTerm(e.target.value)}
  style={{ marginBottom: "10px", width: "100%" }}
  prefix={<SearchOutlined style={{ color: "rgba(0,0,0,0.45)" }} />}
/>
<List
  bordered
  dataSource={filteredMenuItems}
  renderItem={(item) => (
    <List.Item
      key={item._id}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "10px",
      }}
    >
      <span style={{ flex: "1 1 200px" }}>{item.name}</span>

     {getUnitsForItem(item).map((unit) => (
  <div key={unit} style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <span style={{ fontWeight: 500 }}>{unit}:</span>
    <InputNumber
      min={0}
      placeholder="Qty"
      value={quantities[item._id]?.[unit] || ""}
      onChange={(value) =>
        setQuantities((prev) => ({
          ...prev,
          [item._id]: {
            ...prev[item._id],
            [unit]: value,
          },
        }))
      }
      style={{ width: 100 }}
    />
  </div>
))}

    </List.Item>
  )}
/>



          <Button type="primary" htmlType="submit">Add Production</Button>
        </Form>
      </Modal>
    </div>
  );
};

export default Production;
